<template>
  <Teleport to="body">
    <transition name="backdrop-fade" @after-enter="afterEnter">
      <div
        v-if="hasShort"
        ref="modal"
        class="modal-overlay"
        :class="[activeModal]"
        tabindex="0"
        @click="clearShort"
        @keydown.esc="clearShort"
      >
        <transition name="modal-fade">
          <div v-if="hasShort" class="modal" @click.stop>
            <div class="shorts-modal-item">
              <YoutubePlayer
                :is-dragging="false"
                :autoplay="true"
                :active="true"
                :video-id="currentShort?.shortsId"
                :poster="undefined"
                @click="handleVideoClick()"
              />
            </div>
          </div>
        </transition>
        <button class="modal-close" @click="clearShort">
          <Icon name="eenvandaag-general:x-close" />
        </button>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import useShortStore from '~/stores/short';

const showContent = ref(false);
const modal = ref<HTMLElement | null>(null);
const { clearShort } = useShortStore();
const { currentShort, hasShort } = storeToRefs(useShortStore());

watch(hasShort, (value) => {
  bodyScrollLock(value);
});

const afterEnter = (): void => {
  showContent.value = true;
  modal.value?.focus();
};

const activeModal = computed(() => {
  return showContent.value ? 'modal-is--enabled' : null;
});

useFocusTrap(modal, hasShort);

const handleVideoClick = () => {
  return;
};
</script>

<style lang="postcss" scoped>
.modal-overlay {
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  position: fixed;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(0deg, rgba(0, 10, 69, 0.94) 0.03%, rgba(0, 8, 54, 0.94) 99.97%);
  backdrop-filter: blur(16px);
  outline: 0;
  z-index: 99;

  &:focus {
    outline: none;
  }

  &.modal-is--enabled {
    touch-action: auto;
  }

  @media (--sm-viewport) {
    @media screen and (orientation: landscape) {
      padding: var(--modal-padding-top-bottom) var(--modal-padding-left-right);
    }
  }
}

.modal-close {
  --modal-close-size: var(--unit-24);
  --modal-close-color: hsl(var(--color-neutral-white));
  --modal-close-position: var(--unit-18);

  position: absolute;
  display: flex;
  font-size: var(--modal-close-size);
  top: var(--modal-close-position);
  right: var(--modal-close-position);
  color: var(--modal-close-color);
  cursor: pointer;
  padding: 0;
  background: none;
  border: none;
  transition: color var(--modal-close-icon-duration) ease;

  @media (--lg-viewport) {
    --modal-close-size: var(--unit-28);
    --modal-close-position: var(--unit-32);
  }
}

.modal {
  height: 100%;
}

.backdrop-fade-enter-active,
.backdrop-fade-enter,
.backdrop-fade-leave-active {
  transition: opacity 300ms ease;
}

.backdrop-fade-leave-to,
.backdrop-fade-enter-from {
  opacity: 0;
}

.modal-fade-enter-active,
.modal-fade-enter,
.modal-fade-leave-active {
  transition: opacity 300ms ease;
}

.modal-fade-leave-to,
.modal-fade-enter-from {
  opacity: 0;
}

.shorts-modal-item {
  --shorts-modal-item-width: auto;
  --shorts-modal-item-min-width: 90%;
  --shorts-modal-item-height: 100%;
  --shorts-modal-item-active-box-shadow: 0px 32px 40px 0px rgba(0, 0, 0, 0.35);
  --shorts-modal-active-item-border: 4px solid hsl(var(--color-primary-700));
  --shorts-modal-padding-top-bottom: 12vh;
  --shorts-modal-padding-left-right: var(--unit-10);

  padding: var(--shorts-modal-padding-top-bottom) var(--shorts-modal-padding-left-right);
  height: var(--shorts-modal-item-height);
  width: var(--shorts-modal-item-width);
  flex-shrink: 0;

  min-width: var(--shorts-modal-item-min-width);

  @media (--md-viewport) {
    --shorts-modal-item-min-width: auto;
  }

  :deep(.youtube-container) {
    color: hsl(var(--color-neutral-white));

    transform-origin: center center;
    transition:
      box-shadow 0.2s ease-in-out,
      scale 0.4s ease-in-out;

    pointer-events: auto;
    border: var(--shorts-modal-active-item-border);
    box-shadow: var(--shorts-modal-item-active-box-shadow);
  }
}
</style>
